<template>
  <!--
    내부 운영자용 환불 실패 목록 — 자동 취소 뒤 PG 환불에 실패한 주문(매장 횡단). 조회는 `GET /order?refundPending=true` 하나이고,
    오더엔 내부 계정(admin·mng)이면 서버가 매장을 가로지른다(A3 보고서 §6). 읽기 전용 화면이다 — 주문을 바꾸는 버튼이 없다
    (매장 주문 화면의 상태 변경·취소는 매장 계정의 액션이다). 말·조회 조건은 `utils/orderRefund` 한 곳.
  -->
  <div class="contain" data-testid="refund-pending-page">
    <div class="search_form" ref="search_form">
      <p class="form-field-hint refund-pending__intro">{{ $t(text.pendingListIntro) }}</p>
      <el-form :inline="true" :size="size" label-suffix=":" @submit.native.prevent>
        <el-form-item :label="$t(text.pendingListPeriod)">
          <el-date-picker
            v-model="period"
            type="daterange"
            unlink-panels
            clearable
            value-format="yyyy-MM-dd"
            :range-separator="$t('~')"
            :start-placeholder="$t('시작일')"
            :end-placeholder="$t('종료일')"
            :style="{ width: $device.mobile ? `100%` : `280px` }"></el-date-picker>
        </el-form-item>
      </el-form>
    </div>

    <div class="buttons" ref="buttons">
      <el-button :size="size" icon="el-icon-search" @click="searchFirstPage">{{ $t('검색') }}</el-button>
    </div>

    <el-table
      :height="tableHeight - 48"
      border
      stripe
      ref="table"
      row-key="orderCd"
      :data="tableData"
      :size="size"
      :empty-text="$t(text.pendingListEmpty)"
      :default-sort="{ prop: defaultSort.sortProp, order: defaultSort.sortOrder }"
      @sort-change="handleSortChange">
      <el-table-column
        :label="totalElements.toString()"
        type="index"
        width="55"
        align="center"
        label-class-name="data_cnt"
        :index="indexMethod"></el-table-column>
      <!--
        핵심 칸이 앞이다 — 매장·주문 번호·금액·취소 주체·취소 시각·환불 상태(합 835px)가 1440×900 의 표 폭(약 1137px) 안에서
        가로 스크롤 없이 보인다. 뒤의 참고 칸(매장 ID·주문 시간·판매 방식·주문 상태)만 넘치면 스크롤로 본다. 종전에는 참고 칸이
        앞이라 이 화면의 요점인 환불 상태가 표 밖에 있었다(V3-4). 취소 세 칸의 순서·폭은 매장 주문 목록(`OrderListView`)과 같다.
      -->
      <el-table-column
        :label="$t(text.storeNm)"
        prop="storeNm"
        min-width="150"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column :label="$t('주문 번호')" prop="orderNo" min-width="100" align="center"></el-table-column>
      <el-table-column
        :label="$t('총금액')"
        prop="amt"
        min-width="90"
        align="right"
        :formatter="mainVue.formatterPrice"></el-table-column>
      <el-table-column :label="$t(text.cancelActor)" min-width="130" align="center">
        <template slot-scope="scope">
          <span data-testid="cancel-actor">{{ $t(cancelActorLabel(scope.row.cancelActor)) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t(text.cancelDate)"
        prop="cancelDate"
        min-width="140"
        align="center"
        sortable="custom"></el-table-column>
      <el-table-column :label="$t(text.refundStatus)" min-width="170" align="center">
        <template slot-scope="scope">
          <OrderRefundStatus :status="scope.row.refundStatus" :fail-reason="scope.row.cancelPayFailReason" />
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.storeId)" prop="storeId" min-width="110" align="center"></el-table-column>
      <el-table-column
        :label="$t('주문 시간')"
        prop="orderDate"
        min-width="140"
        align="center"
        sortable="custom"></el-table-column>
      <el-table-column :label="$t('판매 방식')" prop="salesTp.nmLan" min-width="80" align="center"></el-table-column>
      <el-table-column
        :label="$t('주문 상태')"
        prop="orderStatus.nmLan"
        min-width="80"
        align="center"></el-table-column>
    </el-table>

    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>
  </div>
</template>

<script>
import OrderRefundStatus from '@/components/order/list/OrderRefundStatus.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { initData } from '@/utils/constants'
import {
  ORDER_REFUND_TEXT,
  REFUND_PENDING_DEFAULT_SORT,
  cancelActorLabel,
  refundPendingListQuery,
} from '@/utils/orderRefund'
import { withPaging } from '@/utils/pagedList'

export default {
  name: 'OrderRefundPendingView',

  mixins: [mainVueMixin, pagedListMixin],

  components: { OrderRefundStatus },

  data: function () {
    var data = initData()
    data.url = '/order'
    data.text = ORDER_REFUND_TEXT
    data.defaultSort = REFUND_PENDING_DEFAULT_SORT
    data.searchForm = Object.assign(withPaging({}), REFUND_PENDING_DEFAULT_SORT)
    /** 주문 기간 — 비우면 전체(서버 술어가 배포 뒤 취소만 자른다) */
    data.period = null
    /** 주문 상태 코드 전부 — 서버가 `orderStatusCds` 를 요구한다(없으면 조회를 낼 수 없다) */
    data.orderStatusCds = []

    return data
  },

  created: function () {
    this.searchFirstPage()
  },

  methods: {
    cancelActorLabel,

    /**
     * **이 화면의 조회 입구 한 곳**(최초 진입·검색 버튼) — 믹스인의 같은 이름을 덮어쓴다. 상태 코드를 모르면 먼저 **한 번** 읽고,
     * 알게 됐을 때만 1페이지를 **한 번** 묻는다. 읽기가 실패했거나 빈 목록이면 거기서 멈춘다 — 다시 시도는 사람이 검색 버튼으로 한다.
     *
     * 종전에는 전제 판정([canSearch])이 읽기를 일으키고 읽기가 다시 이 입구를 불러, 코드가 빈 목록(성공 응답)이면 읽기 → 조회
     * 시도 → 판정 → 읽기 … 가 사람 조작 없이 끝없이 돌았다(요청·전역 로딩 반복). 읽기는 조회를 부르지 않고, 판정은 읽기를
     * 부르지 않는다 — 되풀이의 고리가 구조에 없다.
     */
    searchFirstPage: async function () {
      const ready = this.canSearch() || (await this.loadOrderStatuses())

      if (ready) this.fromFirstPage()
    },

    /**
     * 주문 상태 코드 전부를 읽는다 — **조회는 하지 않는다**(입구가 한다). 답: 이제 조회할 수 있는가.
     *
     * 요청 실패는 헬퍼·인터셉터가 이미 알렸다. 성공인데 빈 목록이면 아무도 말하지 않았으므로 여기서 알린다.
     *
     * @returns {Promise<boolean>}
     */
    loadOrderStatuses: async function () {
      const outcome = await this.mainVue.$axios.search(
        '/code/getCode/ORDER_STATUS',
        null,
        function (result) {
          this.orderStatusCds = (Array.isArray(result) ? result : []).map((code) => code.cd)
        }.bind(this)
      )

      if (outcome.ok && !this.canSearch()) {
        this.mainVue.$message({ type: 'warning', message: this.$t(this.text.statusCodesEmpty) })
      }

      return this.canSearch()
    },

    /**
     * 조회의 전제 — 상태 코드를 안다. **판정만 한다**(읽기·안내 없음): 페이지·정렬·크기 변경이 이것만 묻고, 모르면 아무것도 바꾸지
     * 않는다(`pagedListMixin.canSearch`). 코드를 다시 읽는 것은 [searchFirstPage] 한 곳이다.
     */
    canSearch: function () {
      return this.orderStatusCds.length > 0
    },

    search: function () {
      const query = refundPendingListQuery({
        period: this.period,
        orderStatusCds: this.orderStatusCds,
        paging: this.searchQuery(),
      })
      if (!query) return

      this.mainVue.$axios.search(
        this.url,
        query,
        function (result) {
          this.applyPage(result)
        }.bind(this)
      )
    },
  },
}
</script>

<style scoped>
.refund-pending__intro {
  margin: 0 0 8px;
}
</style>
